import React, { useMemo } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, Target } from 'lucide-react';

/**
 * Composant de visualisation radar des compétences
 * Affiche un graphique radar avec les 3 catégories de compétences
 */
const CompetencesRadarChart = ({ evaluations = [], options = {} }) => {
  
  // Calculer les moyennes par catégorie
  const chartData = useMemo(() => {
    const niveauToScore = {
      'non_acquis': 1,
      'en_cours': 2,
      'acquis': 3,
      'maitrise': 4
    };

    const calculateCategoryAverage = (categoryEvaluations, categoryOptions) => {
      if (!categoryEvaluations || categoryEvaluations.length === 0) return 0;
      
      let totalScore = 0;
      let count = 0;
      
      categoryOptions?.forEach(compName => {
        const eval_ = categoryEvaluations.find(e => e.competence === compName);
        if (eval_) {
          // Utiliser niveau_valide si disponible, sinon niveau_auto
          const niveau = eval_.niveau_valide || eval_.niveau_auto || 'non_acquis';
          totalScore += niveauToScore[niveau] || 1;
          count++;
        }
      });
      
      return count > 0 ? (totalScore / count) : 0;
    };

    // Séparer les évaluations par catégorie
    const transversalesEvals = evaluations.filter(e => e.categorie === 'transversale');
    const techniquesEvals = evaluations.filter(e => e.categorie === 'technique');
    const personnellesEvals = evaluations.filter(e => e.categorie === 'personnelle');

    const avgTransversales = calculateCategoryAverage(transversalesEvals, options.competences_transversales);
    const avgTechniques = calculateCategoryAverage(techniquesEvals, options.competences_techniques);
    const avgPersonnelles = calculateCategoryAverage(personnellesEvals, options.competences_personnelles);

    return [
      {
        category: 'Transversales',
        fullName: 'Compétences Transversales (Savoir-être)',
        score: avgTransversales,
        fullMark: 4,
        color: '#8b5cf6'
      },
      {
        category: 'Techniques',
        fullName: 'Compétences Techniques (Savoir-faire)',
        score: avgTechniques,
        fullMark: 4,
        color: '#3b82f6'
      },
      {
        category: 'Personnelles',
        fullName: 'Compétences Personnelles (Savoir-devenir)',
        score: avgPersonnelles,
        fullMark: 4,
        color: '#10b981'
      }
    ];
  }, [evaluations, options]);

  // Calculer le score global
  const globalScore = useMemo(() => {
    const validScores = chartData.filter(d => d.score > 0);
    if (validScores.length === 0) return 0;
    return validScores.reduce((sum, d) => sum + d.score, 0) / validScores.length;
  }, [chartData]);

  const getScoreLabel = (score) => {
    if (score >= 3.5) return { text: 'Excellent', color: 'text-emerald-600' };
    if (score >= 2.5) return { text: 'Bon niveau', color: 'text-blue-600' };
    if (score >= 1.5) return { text: 'En progression', color: 'text-amber-600' };
    return { text: 'À développer', color: 'text-red-600' };
  };

  const scoreInfo = getScoreLabel(globalScore);

  // Vérifier s'il y a des données
  const hasData = chartData.some(d => d.score > 0);

  if (!hasData) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5 text-blue-600" />
            Profil de Compétences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <TrendingUp className="w-12 h-12 mb-3 text-slate-300" />
            <p className="text-center">Aucune évaluation disponible</p>
            <p className="text-sm text-slate-400 mt-1">
              Les compétences apparaîtront une fois évaluées
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-3xl shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5 text-blue-600" />
            Profil de Compétences
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Score global:</span>
            <span className={`text-lg font-bold ${scoreInfo.color}`}>
              {globalScore.toFixed(1)}/4
            </span>
            <span className={`text-xs px-2 py-1 rounded-full ${
              globalScore >= 3.5 ? 'bg-emerald-100 text-emerald-700' :
              globalScore >= 2.5 ? 'bg-blue-100 text-blue-700' :
              globalScore >= 1.5 ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {scoreInfo.text}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
              <PolarGrid 
                stroke="#e2e8f0" 
                strokeDasharray="3 3"
              />
              <PolarAngleAxis 
                dataKey="category" 
                tick={{ fill: '#475569', fontSize: 12, fontWeight: 500 }}
                tickLine={false}
              />
              <PolarRadiusAxis 
                angle={30} 
                domain={[0, 4]} 
                tick={{ fill: '#94a3b8', fontSize: 10 }}
                tickCount={5}
                axisLine={false}
              />
              <Radar
                name="Niveau actuel"
                dataKey="score"
                stroke="#2563eb"
                fill="#3b82f6"
                fillOpacity={0.4}
                strokeWidth={2}
                dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg">
                        <p className="font-semibold text-slate-900 text-sm">{data.fullName}</p>
                        <p className="text-slate-600 text-sm mt-1">
                          Score moyen: <span className="font-bold text-blue-600">{data.score.toFixed(2)}/4</span>
                        </p>
                        <div className="mt-2 text-xs text-slate-500">
                          {data.score >= 3.5 ? '★★★★ Maîtrisé' :
                           data.score >= 2.5 ? '★★★☆ Acquis' :
                           data.score >= 1.5 ? '★★☆☆ En cours' :
                           '★☆☆☆ À développer'}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Légende détaillée */}
        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-100">
          {chartData.map((item, idx) => (
            <div key={idx} className="text-center p-3 rounded-xl bg-slate-50">
              <div className={`w-3 h-3 rounded-full mx-auto mb-2 ${
                item.category === 'Transversales' ? 'bg-purple-500' :
                item.category === 'Techniques' ? 'bg-blue-500' :
                'bg-emerald-500'
              }`}></div>
              <p className="text-xs font-medium text-slate-700">{item.category}</p>
              <p className={`text-lg font-bold ${
                item.score >= 3 ? 'text-emerald-600' :
                item.score >= 2 ? 'text-blue-600' :
                item.score >= 1 ? 'text-amber-600' :
                'text-slate-400'
              }`}>
                {item.score > 0 ? item.score.toFixed(1) : '-'}
              </p>
            </div>
          ))}
        </div>

        {/* Échelle de référence */}
        <div className="flex justify-center gap-4 mt-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> 1: Non acquis</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400"></span> 2: En cours</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400"></span> 3: Acquis</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400"></span> 4: Maîtrisé</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompetencesRadarChart;
